import React from "react";
import { formatMoneyFull } from "../../../../../utils/formatters";
import CapitalAllocationReferenceCards from "./CapitalAllocationReferenceCards";
import CapitalAllocationPositionTable from "./CapitalAllocationPositionTable";

const MESSAGES = {
  ERROR: "The optimal capital requirement could not be calculated. The economic reference is currently unavailable.",
  UNAVAILABLE: "The optimal capital requirement is not currently available. The available capital decision cannot yet be benchmarked against an authoritative unconstrained portfolio.",
  INCOMPLETE_EVIDENCE: "The economically optimal capital requirement could not be established from the available evidence.",
  INVALID_CONTRACT: "The capital allocation overview contract is structurally inconsistent.",
};

const POLICY_METADATA_NOTE = "Investment-policy metadata is not currently available. The capital requirement shown above comes from the authoritative unconstrained portfolio result.";

const displayLabel = (value) => value == null ? null : String(value).toLowerCase().split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

export default function CapitalAllocationOverview({
  loading = false,
  error = null,
  unconstrainedResult,
  policy,
  currency,
  comparisonHorizon,
  capitalBasis,
}) {
  const resolvedCurrency = unconstrainedResult?.currency || currency;
  let status = "AVAILABLE";
  if (loading) status = "LOADING";
  else if (error) status = "ERROR";
  else if (!unconstrainedResult) status = "UNAVAILABLE";
  else if (
    typeof unconstrainedResult !== "object" ||
    Array.isArray(unconstrainedResult) ||
    !unconstrainedResult.optimalPortfolio ||
    typeof unconstrainedResult.optimalPortfolio !== "object" ||
    Array.isArray(unconstrainedResult.optimalPortfolio)
  ) status = "INVALID_CONTRACT";
  else if (unconstrainedResult.status !== "OPTIMAL") status = "INCOMPLETE_EVIDENCE";
  else if (
    unconstrainedResult.optimalPortfolio.investment == null ||
    typeof unconstrainedResult.optimalPortfolio.investment !== "number" ||
    !Number.isFinite(unconstrainedResult.optimalPortfolio.investment) ||
    typeof resolvedCurrency !== "string" ||
    !/^[A-Z]{3}$/.test(resolvedCurrency)
  ) status = "INCOMPLETE_EVIDENCE";

  const formattedCapital = status === "AVAILABLE"
    ? formatMoneyFull(unconstrainedResult.optimalPortfolio.investment, resolvedCurrency, 0)
    : null;
  const horizon = displayLabel(comparisonHorizon);
  const basis = displayLabel(capitalBasis);
  const policyId = policy?.policyId;
  const policyVersion = policy?.policyVersion;
  const availablePolicyMetadata = [policyId, policyVersion, horizon, basis].filter(Boolean).length;
  const policyMetadataStatus = availablePolicyMetadata === 0
    ? "UNAVAILABLE"
    : availablePolicyMetadata === 4 ? "AVAILABLE" : "INCOMPLETE";

  return (
    <section aria-labelledby="capital-allocation-overview-title">
      <div className="max-w-4xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Pre-budget reference</p>
        <h3 id="capital-allocation-overview-title" className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Capital Requirement Reference</h3>
        <p className="mt-2 text-sm font-medium text-slate-600">Reference positions before defining the available capital budget.</p>
      </div>
      {status !== "AVAILABLE" ? (
        <div role={status === "ERROR" ? "alert" : "status"} className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-5 py-6 text-sm leading-6 text-slate-600">{status === "LOADING" ? "Calculating economic reference…" : MESSAGES[status]}</div>
      ) : <>
      <CapitalAllocationReferenceCards formattedCapital={formattedCapital} currency={resolvedCurrency} horizon={horizon} />
      {policyMetadataStatus !== "UNAVAILABLE" && <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-sm">
        {(policyId || policyVersion) && <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Policy</dt><dd className="mt-1 font-medium text-slate-800">{[policyId, policyVersion && `v${policyVersion}`].filter(Boolean).join(" ")}</dd></div>}
        {horizon && <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Review Period</dt><dd className="mt-1 font-medium text-slate-800">{horizon}</dd></div>}
        {basis && <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Capital Basis</dt><dd className="mt-1 font-medium text-slate-800">{basis}</dd></div>}
      </dl>}
      {policyMetadataStatus === "UNAVAILABLE" && <p className="mt-5 text-sm leading-6 text-slate-500">{POLICY_METADATA_NOTE}</p>}
      {policyMetadataStatus === "INCOMPLETE" && <p className="mt-3 text-sm leading-6 text-slate-500">Some investment-policy metadata is not currently available.</p>}
      <CapitalAllocationPositionTable formattedCapital={formattedCapital} />
      <aside aria-labelledby="capital-allocation-note-title" className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-5">
        <h4 id="capital-allocation-note-title" className="text-sm font-semibold text-slate-900">How to interpret this reference</h4>
        <div className="mt-2 space-y-2 text-sm leading-6 text-slate-600"><p>The Optimal Capital Requirement is not the approved budget.</p><p>It represents the capital benchmark before any available-capital constraint is applied.</p><p>Use this reference to determine the capital available for the constrained portfolio evaluation below.</p></div>
      </aside>
      </>}
    </section>
  );
}
